import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    ArrowLeft,
    Check,
    Loader2,
    AlertCircle,
    UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import ProjectHeader from "../components/ProjectHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";

/**
 * iter-55 Bulk Cast Edit.
 *
 * Productions need to assign character numbers + descriptions across many
 * characters quickly after a script import. Going one-by-one through
 * Cast → Edit dialog is too slow.
 *
 * This page renders every character in the project as an inline-editable
 * row (number + actor + notes). Each field auto-saves on blur; an aria-
 * live "Saved" pip flashes per row so users get immediate feedback.
 *
 * Sorting: by import order (default), by character_number (numeric where
 * possible), or alphabetical by name. After a numbering pass users will
 * usually flip to "By number" to verify the order looks right.
 *
 * Owner + admin + any member with the `cast_edit` cap can reach this
 * page (Iter-106). Members without the cap get redirected back to /cast
 * with a friendly toast.
 */

const SORT_KEYS = ["number", "name"];
const sortLabels = {
    number: "By number",
    name: "By name",
};

function sortCharacters(list, sort) {
    const copy = [...list];
    if (sort === "name") {
        copy.sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, {
                sensitivity: "base",
            }),
        );
    } else if (sort === "number") {
        copy.sort((a, b) => {
            const an = (a.character_number || "").trim();
            const bn = (b.character_number || "").trim();
            // Empty numbers go to the end so users see what's still
            // unnumbered.
            if (!an && !bn) return 0;
            if (!an) return 1;
            if (!bn) return -1;
            const ai = parseInt(an, 10);
            const bi = parseInt(bn, 10);
            if (Number.isFinite(ai) && Number.isFinite(bi) && ai !== bi) {
                return ai - bi;
            }
            return an.localeCompare(bn, undefined, { numeric: true });
        });
    } else {
        // Unrecognised sort key — fall back to name sort.
        copy.sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, {
                sensitivity: "base",
            }),
        );
    }
    return copy;
}

export function EditableField({
    value,
    onSave,
    placeholder,
    testid,
    multiline = false,
    inputMode,
    maxLength,
    className = "",
}) {
    const [draft, setDraft] = useState(value || "");
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(0);
    const [error, setError] = useState("");
    const baseRef = useRef(value || "");

    useEffect(() => {
        // Sync external updates (e.g. when sort changes and the same
        // record renders in a new row).
        if ((value || "") !== baseRef.current) {
            baseRef.current = value || "";
            setDraft(value || "");
        }
    }, [value]);

    const commit = async () => {
        const next = (draft || "").trim();
        const prev = (baseRef.current || "").trim();
        if (next === prev) return;
        setSaving(true);
        setError("");
        try {
            await onSave(next);
            baseRef.current = next;
            setSavedAt(Date.now());
        } catch (e) {
            setError(formatApiError(e));
            // revert to last known-good value on failure
            setDraft(baseRef.current || "");
        } finally {
            setSaving(false);
        }
    };

    const onKeyDown = (e) => {
        if (multiline) return;
        if (e.key === "Enter") {
            e.preventDefault();
            e.target.blur();
        }
    };

    const Tag = multiline ? "textarea" : "input";

    return (
        <div className="relative">
            <Tag
                data-testid={testid}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                inputMode={inputMode}
                maxLength={maxLength}
                rows={multiline ? 2 : undefined}
                className={[
                    "w-full bg-secondary border border-transparent rounded-md px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
                    multiline ? "resize-none leading-snug" : "h-10",
                    error ? "border-red-500/60" : "",
                    className,
                ]
                    .filter(Boolean)
                    .join(" ")}
            />
            {saving ? (
                <Loader2
                    size={12}
                    className="absolute right-2 top-2.5 text-neutral-400 animate-spin"
                />
            ) : savedAt && Date.now() - savedAt < 1500 ? (
                <Check
                    size={12}
                    className="absolute right-2 top-2.5 text-emerald-300"
                />
            ) : null}
            {error ? (
                <p className="mt-1 text-[11px] text-red-400">{error}</p>
            ) : null}
        </div>
    );
}

export default function BulkCastEdit() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();

    const [project, setProject] = useState(null);
    const [characters, setCharacters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [sort, setSort] = useState("name");
    const [filter, setFilter] = useState("");

    // iter-65 #3: default to "number" once at least one character has
    // a character_number (matches CastHub behaviour).
    useEffect(() => {
        if (characters.some((c) => (c.character_number || "").trim())) {
            setSort((cur) => (cur === "name" ? "number" : cur));
        }
    }, [characters]);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // Iter-106 — pull the current user's resolved capabilities
                // alongside the project so admins and members with the
                // `cast_edit` cap can reach the bulk editor (previously
                // owner-only — locked admins out on mobile + desktop alike).
                const [{ data: p }, { data: chars }, { data: perms }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`),
                    api.get(`${apiBase}/${projectId}/characters`),
                    api
                        .get(`${apiBase}/${projectId}/permissions/me`)
                        .catch(() => ({ data: { permissions: {} } })),
                ]);
                if (!alive) return;
                const role = p?.role;
                const canEdit =
                    role === "owner" ||
                    role === "admin" ||
                    Boolean(perms?.permissions?.cast_edit);
                if (!canEdit) {
                    toast.error("You don't have permission to bulk-edit cast.");
                    navigate(`${routeBase}/${projectId}/cast`, { replace: true });
                    return;
                }
                setProject(p);
                setCharacters(chars || []);
            } catch (e) {
                if (alive) setError(formatApiError(e));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [projectId, navigate, apiBase]);

    const visible = useMemo(() => {
        const sorted = sortCharacters(characters, sort);
        const needle = filter.trim().toLowerCase();
        if (!needle) return sorted;
        return sorted.filter((c) => {
            const hay = [c.name, c.actor, c.character_number, c.notes]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(needle);
        });
    }, [characters, sort, filter]);

    const updateField = async (id, patch) => {
        const { data } = await api.patch(
            `${apiBase}/${projectId}/characters/${id}`,
            patch,
        );
        setCharacters((prev) =>
            prev.map((c) => (c.id === id ? { ...c, ...data } : c)),
        );
    };

    const numberedCount = characters.filter(
        (c) => (c.character_number || "").trim() !== "",
    ).length;

    return (
        <div data-testid="bulk-cast-page" className="min-h-screen">
            <ProjectHeader
                projectName={project?.name}
                subtitle="Bulk cast edit"
            />

            {/* Iter-118.9 — body container standardised to the
                shared `max-w-2xl mx-auto px-5` primitive. */}
            <main className="max-w-2xl mx-auto px-5 pt-5 pb-32 w-full">
                <Link
                    data-testid="bulk-cast-back-link"
                    to={`${routeBase}/${projectId}/cast`}
                    className="tap inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-4"
                >
                    <ArrowLeft size={14} />
                    Back to Cast
                </Link>

                <div className="flex items-start gap-3 mb-5">
                    <div className="h-10 w-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                        <UserRound size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1
                            data-testid="bulk-cast-title"
                            className="font-display text-2xl sm:text-3xl font-black text-white leading-none"
                        >
                            Bulk cast edit
                        </h1>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1.5">
                            <span data-testid="bulk-cast-count">
                                {characters.length} characters
                            </span>
                            {" · "}
                            <span data-testid="bulk-cast-numbered-count">
                                {numberedCount} numbered
                            </span>
                        </p>
                    </div>
                    {/* iter-57: prominent top Save action. Each field
                        already auto-saves on blur, so this button's job
                        is to confirm "I'm done editing — take me back to
                        Cast." Mirrored at the bottom of the page. */}
                    <Button
                        type="button"
                        data-testid="bulk-cast-save-top"
                        onClick={() =>
                            navigate(`${routeBase}/${projectId}/cast`, {
                                state: { lens: "onset" },
                            })
                        }
                        className="shrink-0 h-10 px-4 font-semibold"
                    >
                        <Check size={14} className="mr-1.5" />
                        Save
                    </Button>
                </div>

                <p className="rounded-md border border-emerald-400/20 bg-emerald-400/5 text-emerald-200/80 text-[11px] px-3 py-2 mb-4 leading-relaxed">
                    Each field auto-saves the moment you blur it (or press
                    <kbd className="font-mono mx-1 px-1 rounded bg-secondary text-emerald-100 border border-white/10">Enter</kbd>).
                    Tap <strong className="font-semibold">Save</strong> when
                    you&rsquo;re done to return to the Cast view.
                </p>

                {error ? (
                    <p
                        data-testid="bulk-cast-error"
                        className="rounded-md border border-red-500/30 bg-red-500/10 text-red-200 text-sm px-3 py-2 mb-4"
                    >
                        {error}
                    </p>
                ) : null}

                <div className="sticky top-14 z-30 -mx-4 px-4 py-2.5 bg-background/95 backdrop-blur border-b border-white/10 mb-3">
                    <div className="flex items-center gap-2">
                        <Input
                            data-testid="bulk-cast-filter"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter by name, actor or number"
                            className="h-10 bg-secondary border-transparent text-sm"
                        />
                        <div className="flex items-center rounded-md bg-secondary border border-white/10 overflow-hidden shrink-0">
                            {SORT_KEYS.map(
                                (k, i) => (
                                    <button
                                        key={k}
                                        type="button"
                                        data-testid={`bulk-cast-sort-${k}`}
                                        onClick={() => setSort(k)}
                                        className={[
                                            "h-10 px-3 text-[11px] font-semibold uppercase tracking-wider transition-colors",
                                            sort === k
                                                ? "bg-primary text-primary-foreground"
                                                : "text-neutral-300 hover:bg-white/10",
                                            i > 0 ? "border-l border-white/10" : "",
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    >
                                        {sortLabels[k]}
                                    </button>
                                ),
                            )}
                        </div>
                    </div>
                    <p className="mt-1.5 text-[11px] text-neutral-500">
                        Tap any field to edit. Changes save when you blur the
                        field or press <kbd className="font-mono">Enter</kbd>.
                    </p>
                </div>

                {loading ? (
                    <div
                        data-testid="bulk-cast-loading"
                        className="space-y-3"
                    >
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div
                                key={i}
                                className="h-28 rounded-xl border border-white/10 bg-card animate-pulse"
                            />
                        ))}
                    </div>
                ) : characters.length === 0 ? (
                    <div
                        data-testid="bulk-cast-empty"
                        className="rounded-xl border border-dashed border-white/10 p-10 text-center"
                    >
                        <UserRound
                            size={28}
                            className="mx-auto text-neutral-500"
                        />
                        <p className="mt-3 text-sm text-neutral-300">
                            No cast yet.
                        </p>
                        <p className="text-xs text-neutral-500 mt-1">
                            Import a script in Settings → Script setup, or add
                            characters from the Cast hub.
                        </p>
                    </div>
                ) : visible.length === 0 ? (
                    <div
                        data-testid="bulk-cast-no-matches"
                        className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                    >
                        <AlertCircle
                            size={20}
                            className="mx-auto text-neutral-500"
                        />
                        <p className="mt-2 text-sm text-neutral-400">
                            No characters match &ldquo;{filter}&rdquo;.
                        </p>
                    </div>
                ) : (
                    <ul
                        data-testid="bulk-cast-list"
                        className="space-y-2"
                    >
                        {visible.map((c) => (
                            <li
                                key={c.id}
                                data-testid={`bulk-cast-row-${c.id}`}
                                className="rounded-xl border border-white/10 bg-card p-3 sm:p-4"
                            >
                                <div className="grid grid-cols-[64px_1fr] sm:grid-cols-[72px_1fr_1fr] gap-2 sm:gap-3 items-start">
                                    <div>
                                        <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1">
                                            #
                                        </label>
                                        <EditableField
                                            testid={`bulk-cast-number-${c.id}`}
                                            value={c.character_number}
                                            placeholder="—"
                                            inputMode="numeric"
                                            maxLength={6}
                                            onSave={(next) =>
                                                updateField(c.id, {
                                                    character_number: next,
                                                })
                                            }
                                            className="text-center font-mono"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span
                                                data-testid={`bulk-cast-name-${c.id}`}
                                                className="font-semibold text-white truncate"
                                            >
                                                {c.name}
                                            </span>
                                            {c.team === "crowd" ? (
                                                <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-200">
                                                    Crowd
                                                </span>
                                            ) : null}
                                        </div>
                                        <EditableField
                                            testid={`bulk-cast-actor-${c.id}`}
                                            value={c.actor}
                                            placeholder="Actor / casting"
                                            maxLength={120}
                                            onSave={(next) =>
                                                updateField(c.id, { actor: next })
                                            }
                                        />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1 min-w-0">
                                        <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1 sm:hidden">
                                            Notes
                                        </label>
                                        <EditableField
                                            testid={`bulk-cast-notes-${c.id}`}
                                            value={c.notes}
                                            placeholder="Description / wardrobe notes"
                                            multiline
                                            maxLength={2000}
                                            onSave={(next) =>
                                                updateField(c.id, { notes: next })
                                            }
                                        />
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="mt-6 text-center">
                    <Button
                        type="button"
                        data-testid="bulk-cast-save-bottom"
                        onClick={() =>
                            navigate(`${routeBase}/${projectId}/cast`, {
                                state: { lens: "onset" },
                            })
                        }
                        className="min-tap font-semibold"
                    >
                        <Check size={14} className="mr-1.5" />
                        Save changes
                    </Button>
                </div>
            </main>
        </div>
    );
}
